import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './styles/tokens.css';
import { AuthProvider, useAuth } from './lib/auth';
import { AppShell } from './components/AppShell';
import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import StationsPage from './pages/Stations';
import VariantsPage from './pages/Variants';
import OrdersPage from './pages/Orders';
import OrderDetailPage from './pages/OrderDetail';
import UsersPage from './pages/Users';
import AuthFilesPage from './pages/AuthFiles';
import TransfersPage from './pages/Transfers';
import TotpSetupPage from './pages/TotpSetup';
import WholesalePage from './pages/Wholesale';
import WholesaleDetailPage from './pages/WholesaleDetail';
import CupetPage from './pages/Cupet';
import CupetDetailPage from './pages/CupetDetail';
import CupetStockPage from './pages/CupetStock';
import GasPage from './pages/Gas';
import GasDetailPage from './pages/GasDetail';
import GasCatalogPage from './pages/GasCatalog';
import CronRunsPage from './pages/CronRuns';
import CronRunDetailPage from './pages/CronRunDetail';
import TransferDetailPage from './pages/TransferDetail';
import UserDetailPage from './pages/UserDetail';
import AuditsPage from './pages/Audits';
import AuditDetailPage from './pages/AuditDetail';
import { useLocation } from 'react-router-dom';

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1 } } });

function Gated({ children }: { children: React.ReactNode }) {
  const { me, loading, logout } = useAuth();
  const loc = useLocation();
  if (loading) return <FullLoader />;
  if (!me) return <Navigate to="/login" replace />;
  // Admin/operator MUST have 2FA. Force enrolment before serving any other route.
  if ((me.role === 'admin' || me.role === 'operator') && !me.totpEnabled && loc.pathname !== '/2fa-setup') {
    return <Navigate to="/2fa-setup" replace />;
  }
  if (me.role !== 'admin' && me.role !== 'operator') {
    return (
      <div style={{ padding: 80, minHeight: '100vh', display: 'grid', placeItems: 'center', textAlign: 'center' }}>
        <div style={{ maxWidth: 420 }}>
          <h1 style={{ fontSize: 28, fontWeight: 600, margin: '0 0 12px' }}>Sin acceso al backoffice</h1>
          <p style={{ color: 'var(--text-2)', marginBottom: 24 }}>
            Tu cuenta no tiene rol de administrador u operador. Cierra sesión e inicia con una cuenta autorizada.
          </p>
          <button
            onClick={async () => {
              await logout();
              window.location.href = '/login';
            }}
            style={{ background: 'var(--accent)', color: 'var(--accent-ink)', border: 'none', padding: '12px 22px', borderRadius: 12, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }
  return <AppShell>{children}</AppShell>;
}

function FullLoader() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <div style={{ width: 28, height: 28, borderRadius: 999, border: '3px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<Gated><DashboardPage /></Gated>} />
            <Route path="/stations" element={<Gated><StationsPage /></Gated>} />
            <Route path="/variants" element={<Gated><VariantsPage /></Gated>} />
            <Route path="/orders" element={<Gated><OrdersPage /></Gated>} />
            <Route path="/orders/:id" element={<Gated><OrderDetailPage /></Gated>} />
            <Route path="/wholesale" element={<Gated><WholesalePage /></Gated>} />
            <Route path="/wholesale/:code" element={<Gated><WholesaleDetailPage /></Gated>} />
            <Route path="/cupet" element={<Gated><CupetPage /></Gated>} />
            <Route path="/cupet/stock" element={<Gated><CupetStockPage /></Gated>} />
            <Route path="/cupet/:externalId" element={<Gated><CupetDetailPage /></Gated>} />
            <Route path="/gas" element={<Gated><GasPage /></Gated>} />
            <Route path="/gas/catalogo" element={<Gated><GasCatalogPage /></Gated>} />
            <Route path="/gas/:code" element={<Gated><GasDetailPage /></Gated>} />
            <Route path="/users" element={<Gated><UsersPage /></Gated>} />
            <Route path="/users/:id" element={<Gated><UserDetailPage /></Gated>} />
            <Route path="/auth-files" element={<Gated><AuthFilesPage /></Gated>} />
            <Route path="/transfers" element={<Gated><TransfersPage /></Gated>} />
            <Route path="/transfers/:reference" element={<Gated><TransferDetailPage /></Gated>} />
            <Route path="/cron-runs" element={<Gated><CronRunsPage /></Gated>} />
            <Route path="/cron-runs/:id" element={<Gated><CronRunDetailPage /></Gated>} />
            <Route path="/audits" element={<Gated><AuditsPage /></Gated>} />
            <Route path="/audits/:id" element={<Gated><AuditDetailPage /></Gated>} />
            <Route path="/2fa-setup" element={<Gated><TotpSetupPage /></Gated>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
